/** @jsx isolateComponent */

import { isolateComponent, withModifier } from '@zapier/style-encapsulation';
import {
  Text,
  Typography,
  Colors,
  Button,
  Heading,
} from '@zapier/design-system';

import { SizeType } from '../../../types';

type GetHelpCtaProps = {
  /** True if the user is permitted to email support via the contact form. */
  isEmailSupportEligible: boolean;
  /** Called when the "Get Help" button is clicked. */
  onButtonClick: () => void;
  /** The size of the container in which the component is displayed. */
  size: SizeType;
};

const Styles = {
  root: (size: SizeType) => [
    {
      display: 'flex',
      borderRadius: 10,
      background: Colors.gray5,
      padding: '20px 25px',
    },
    withModifier(size, {
      flexDirection: size === 'small' ? 'column' : 'row',
      alignItems: size === 'small' ? 'inherit' : 'center',
    }),
  ],
  textContainer: (size: SizeType) => [
    {
      flex: '1 0',
    },
    withModifier(size, {
      margin: size === 'small' ? '0 0 10px 0' : '0 30px 0 0',
    }),
  ],
  heading: {
    ...Typography.paragraph1ShortBold,
    color: Colors.gray,
    marginBottom: 5,
  },
};

type TextContent = {
  headingText: string;
  bodyText: string;
  buttonText: string;
};

const ineligibleTextContent: TextContent = {
  headingText: 'Get help from Zapier',
  bodyText:
    'Tell us about your problem, and we’ll find the right solution or help you upgrade to email Support',
  buttonText: 'Get help',
};

const eligibleTextContent: TextContent = {
  headingText: 'Contact support',
  bodyText:
    'Tell us about your problem, and we’ll find the right solution or help you email our Support team',
  buttonText: 'Get help',
};

const getTextContent = ({ isEmailSupportEligible }: GetHelpCtaProps) =>
  isEmailSupportEligible ? eligibleTextContent : ineligibleTextContent;

/** Renders the call-to-action that leads to the  Get Help form. */
const GetHelpCta = (props: GetHelpCtaProps) => {
  const { size, onButtonClick } = props;
  const { headingText, bodyText, buttonText } = getTextContent(props);

  return (
    <aside css={Styles.root(size)}>
      <div css={Styles.textContainer(size)}>
        <Heading css={Styles.heading}>{headingText}</Heading>
        <Text color="gray80" tag="p" type="smallPrint1">
          {bodyText}
        </Text>
      </div>
      <Button onClick={onButtonClick}>{buttonText}</Button>
    </aside>
  );
};

export default GetHelpCta;
